import { useRef, useState } from "react";
import { toast } from "sonner";
import { ScanLine, Camera, FileUp, Loader2, Sparkles } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { checkFile } from "@/pages/ProductForm";

export function ScanCard({ onResult }) {
  const api = useApi();
  const cam = useRef();
  const pick = useRef();
  const [busy, setBusy] = useState(false);
  const scan = async (f) => {
    if (!f) return;
    const err = checkFile(f);
    if (err) return toast.error(err);
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api("post", "/scan", fd, { timeout: 90000 });
      onResult(r, f);
      const extra = [r.purchase_price != null && "price", r.seller && "store"].filter(Boolean);
      toast.success(`Details filled in${extra.length ? `, including ${extra.join(" and ")}` : ""}. Please check them before saving.`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onFile = (e) => { const f = e.target.files[0]; e.target.value = ""; scan(f); };
  return (
    <section className="relative overflow-hidden rounded-[26px] border border-ww-emerald/25 bg-[linear-gradient(140deg,#EAF9F4,#EAF9F9)] p-6 sm:p-8" data-testid="scan-card">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-ww-bright/20 blur-3xl" />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-4">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-ww-emerald shadow-sm">{busy ? <Loader2 className="h-6 w-6 animate-spin" /> : <ScanLine className="h-6 w-6" />}</span>
          <div>
            <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ww-navy">Scan a warranty card <Sparkles className="h-4 w-4 text-ww-bright" /></h2>
            <p className="mt-1 text-sm text-ww-muted">{busy ? "Reading your document…" : "Photograph your warranty card or invoice and we'll fill in the product name, brand, dates, price and store for you."}</p>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" disabled={busy} onClick={() => cam.current.click()} className="ww-btn-primary" data-testid="scan-photo-button"><Camera className="h-4 w-4" />Scan Photo</button>
          <button type="button" disabled={busy} onClick={() => pick.current.click()} className="ww-btn-ghost" data-testid="scan-upload-button"><FileUp className="h-4 w-4" />Upload</button>
        </div>
      </div>
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={onFile} data-testid="scan-camera-input" />
      <input ref={pick} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" hidden onChange={onFile} data-testid="scan-file-input" />
      <p className="relative mt-4 text-[11px] text-ww-muted">The image is read by an AI service only to extract these details. Always check the dates before saving.</p>
    </section>
  );
}
